import { z } from "zod";
import { zSegmentDefinition } from "./segment.js";

/**
 * Custom report definitions run through a whitelisted engine — the definition
 * names fields from a served catalog; the server maps them to SQL. Two modes:
 * detail (rows) and summary (one groupBy + aggregates).
 */

export const zReportObject = z.enum(["constituent", "gift"]);
export type ReportObject = z.infer<typeof zReportObject>;

export const zReportAggregate = z.object({
  fn: z.enum(["count", "sum", "avg", "min", "max"]),
  /** numeric field key from the catalog; count ignores it */
  field: z.string().max(60).optional(),
});

export const zReportDefinition = z.object({
  object: zReportObject,
  /** field keys from the catalog, in display order */
  columns: z.array(z.string().max(60)).min(1).max(20),
  /** constituent reports may reuse the segment engine for filtering */
  filters: zSegmentDefinition.optional(),
  /** gift reports use simple field filters */
  giftFilters: z
    .object({
      dateFrom: z.string().optional(),
      dateTo: z.string().optional(),
      fundId: z.string().uuid().optional(),
      campaignId: z.string().uuid().optional(),
      minAmount: z.number().int().optional(),
    })
    .optional(),
  groupBy: z.string().max(60).optional(),
  aggregates: z.array(zReportAggregate).max(5).default([]),
  sort: z.object({ field: z.string().max(60), dir: z.enum(["asc", "desc"]).default("desc") }).optional(),
  limit: z.number().int().min(1).max(1000).default(100),
});
export type ReportDefinition = z.infer<typeof zReportDefinition>;

export const zSaveReport = z.object({
  name: z.string().trim().min(1).max(120),
  description: z.string().max(500).optional(),
  definition: zReportDefinition,
});
export type SaveReport = z.infer<typeof zSaveReport>;

// ---- scheduled reports (roadmap 11) ----

export const REPORT_SCHEDULE_FREQUENCIES = ["daily", "weekly", "monthly"] as const;
export const zReportScheduleFrequency = z.enum(REPORT_SCHEDULE_FREQUENCIES);
export type ReportScheduleFrequency = z.infer<typeof zReportScheduleFrequency>;

export const REPORT_FILE_FORMATS = ["csv", "xlsx", "pdf"] as const;
export const zReportFileFormat = z.enum(REPORT_FILE_FORMATS);
export type ReportFileFormat = z.infer<typeof zReportFileFormat>;

/**
 * Which gift dates a scheduled run covers, resolved in the org timezone on the
 * day the run fires. "report" keeps the saved report's own date filters.
 * Constituent reports have no date filter, so they always render "as of" the run date.
 */
export const REPORT_DATE_RANGES = ["report", "last_7_days", "last_30_days", "previous_month", "month_to_date", "year_to_date"] as const;
export const zReportDateRange = z.enum(REPORT_DATE_RANGES);
export type ReportDateRange = z.infer<typeof zReportDateRange>;
export const REPORT_DATE_RANGE_LABELS: Record<ReportDateRange, string> = {
  report: "As saved in the report",
  last_7_days: "Last 7 days",
  last_30_days: "Last 30 days",
  previous_month: "Previous month",
  month_to_date: "Month to date",
  year_to_date: "Year to date",
};

const zReportScheduleFields = z.object({
  frequency: zReportScheduleFrequency,
  /** 24h "HH:MM" in the org timezone */
  timeOfDay: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, "Use HH:MM (24-hour)"),
  /** 0 = Sunday … 6 = Saturday; weekly only */
  dayOfWeek: z.number().int().min(0).max(6).nullish(),
  /** 1–31; months without that day run on their last day; monthly only */
  dayOfMonth: z.number().int().min(1).max(31).nullish(),
  format: zReportFileFormat,
  dateRange: zReportDateRange.default("report"),
  /** staff users of this org only — external addresses are not accepted */
  recipientUserIds: z.array(z.string().uuid()).min(1, "Pick at least one recipient").max(25),
  enabled: z.boolean().default(true),
});
const refineSchedule = (v: z.infer<typeof zReportScheduleFields>, ctx: z.RefinementCtx) => {
  if (v.frequency === "weekly" && v.dayOfWeek == null) ctx.addIssue({ code: "custom", path: ["dayOfWeek"], message: "Pick a weekday" });
  if (v.frequency === "monthly" && v.dayOfMonth == null) ctx.addIssue({ code: "custom", path: ["dayOfMonth"], message: "Pick a day of the month" });
};
export const zSaveReportSchedule = zReportScheduleFields.superRefine(refineSchedule);
export type SaveReportSchedule = z.infer<typeof zSaveReportSchedule>;
export const zCreateReportSchedule = zReportScheduleFields.extend({ reportId: z.string().uuid() }).superRefine(refineSchedule);
export type CreateReportSchedule = z.infer<typeof zCreateReportSchedule>;

export interface ReportScheduleRecipient {
  userId: string;
  name: string;
  email: string;
}

export interface ReportSchedule {
  id: string;
  reportId: string;
  reportName: string;
  frequency: ReportScheduleFrequency;
  timeOfDay: string;
  dayOfWeek: number | null;
  dayOfMonth: number | null;
  format: ReportFileFormat;
  dateRange: ReportDateRange;
  recipients: ReportScheduleRecipient[];
  enabled: boolean;
  /** IANA zone the time of day is interpreted in (the org's) */
  timezone: string;
  lastRunAt: string | null;
  lastStatus: "sent" | "failed" | null;
  lastError: string | null;
  /** null while disabled */
  nextRunAt: string | null;
  createdAt: string;
}

export interface ReportColumnDef {
  key: string;
  label: string;
  type: "text" | "money" | "number" | "date" | "datetime";
  numeric?: boolean;
  groupable?: boolean;
}

// ---- retention dashboard (roadmap 10) ----

/**
 * Donor status for a fiscal year Y (reversed gifts never count):
 *  - new:        first-ever gift falls in Y
 *  - retained:   gave in Y and in Y-1
 *  - recaptured: gave in Y, not in Y-1, but in some year before Y-1
 *  - lapsed:     gave in Y-1 but not in Y
 * new + retained + recaptured = donors in Y.
 */
export const RETENTION_STATUSES = ["new", "retained", "recaptured", "lapsed"] as const;
export const zRetentionStatus = z.enum(RETENTION_STATUSES);
export type RetentionStatus = z.infer<typeof zRetentionStatus>;

export const zRetentionDashboardQuery = z.object({
  fiscalYear: z.coerce.number().int().min(1900).max(2100).optional(),
  /** how many first-gift cohorts to show (most recent first-gift years) */
  cohorts: z.coerce.number().int().min(1).max(25).default(8),
  /** scope to one designation (split gifts count only the share allocated there) */
  fundId: z.string().uuid().optional(),
  campaignId: z.string().uuid().optional(),
});
export type RetentionDashboardQuery = z.infer<typeof zRetentionDashboardQuery>;

export const zRetentionDonorsQuery = z.object({
  fiscalYear: z.coerce.number().int().min(1900).max(2100),
  status: zRetentionStatus,
  limit: z.coerce.number().int().min(1).max(1000).default(500),
  fundId: z.string().uuid().optional(),
  campaignId: z.string().uuid().optional(),
});
export type RetentionDonorsQuery = z.infer<typeof zRetentionDonorsQuery>;

export interface RetentionCohortRow {
  /** fiscal year of the cohort's first gift */
  cohort: number;
  donors: number;
  /** average lifetime giving (cents) of the cohort, from constituents.lifetime_giving */
  avgLifetimeGiving: number;
  /** one cell per following fiscal year through the current one; offset 1 = the next FY */
  years: { offset: number; fiscalYear: number; retained: number; rate: number }[];
}

export interface RetentionDashboard {
  fiscalYear: number;
  currentFiscalYear: number;
  fiscalYearStartMonth: number;
  /** inclusive start / exclusive end of the chosen fiscal year */
  start: string;
  end: string;
  counts: Record<RetentionStatus, number> & { donors: number; priorDonors: number };
  /** retained / donors in Y-1, as a percentage with one decimal; null when Y-1 had no donors */
  retentionRate: number | null;
  /** average lifetime giving (cents) across every constituent with a live gift */
  averageLifetimeValue: number;
  lifetimeDonors: number;
  cohorts: RetentionCohortRow[];
}

export interface RetentionDonorRow {
  constituentId: string;
  displayName: string;
  /** live giving in the chosen FY (cents) */
  fiscalYearTotal: number;
  /** live giving in the prior FY (cents) */
  priorYearTotal: number;
  lifetimeGiving: number;
  firstGiftDate: string | null;
  lastGiftDate: string | null;
}

export interface RetentionDonorsResult {
  fiscalYear: number;
  status: RetentionStatus;
  total: number;
  donors: RetentionDonorRow[];
}

export interface ReportResult {
  columns: ReportColumnDef[];
  rows: Record<string, string | number | null>[];
  total: number;
  truncated: boolean;
}
